import { rqsContentSchema } from '@sigora/domain';
import { useEffect, useState } from 'react';

import { fetchArtifact } from '../api/client.ts';

/**
 * The research questions the study fielded, read once the answers start being read, with nothing
 * to watch after: a launched study is frozen (the database refuses a new version of a locked or
 * launched study's documents, `SG002`), and it was paid for only with every document approved, so
 * the current version is the approved one. The read is the by-kind artifact read, which carries
 * only the document, and its content is parsed here. A failed read, an unparseable document, or a
 * current version that is not the approved one leaves the list out and the headline standing.
 */
export const useResearchQuestions = ({
  studyId,
  active,
}: {
  studyId: string;
  active: boolean;
}): readonly { id: string; text: string }[] | null => {
  const [read, setRead] = useState<{
    studyId: string;
    items: readonly { id: string; text: string }[] | null;
  } | null>(null);
  const asked = read?.studyId === studyId;

  useEffect(() => {
    if (!active || asked) {
      return undefined;
    }

    let current = true;
    fetchArtifact(studyId, 'rqs').then(
      (artifact) => {
        if (!current) {
          return;
        }

        const content = rqsContentSchema.safeParse(artifact.content);
        setRead({
          studyId,
          items:
            content.success && artifact.approvedVersion === artifact.version
              ? content.data.items.map(({ id, text }) => ({ id, text }))
              : null,
        });
      },
      () => {
        if (current) {
          setRead({ studyId, items: null });
        }
      },
    );

    return () => {
      current = false;
    };
  }, [studyId, active, asked]);

  return read?.studyId === studyId ? read.items : null;
};
